const pct = (made, attempted) => {
  if (!attempted) return '0.0%';
  return `${((made / attempted) * 100).toFixed(1)}%`;
};

const fg = (made, attempted) => `${made || 0}/${attempted || 0}`;

const ensurePageSpace = (doc, currentY, needed, margin) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + needed <= pageHeight - margin) return currentY;
  doc.addPage();
  return margin;
};

const drawSectionTitle = (doc, title, y, margin) => {
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(11, 61, 145);
  doc.text(title, margin, y);
  doc.setTextColor(0, 0, 0);
  return y + 8;
};

const drawTeamComparison = (doc, homeTeam, awayTeam, gameStats, startY, margin) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const colStat = margin;
  const colHome = margin + 90;
  const colAway = margin + 170;
  let y = startY;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(11, 61, 145);
  doc.rect(margin, y - 5, pageWidth - margin * 2, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('Statistic', colStat + 2, y);
  doc.text(homeTeam?.shortName || homeTeam?.name || 'Home', colHome, y);
  doc.text(awayTeam?.shortName || awayTeam?.name || 'Away', colAway, y);
  doc.setTextColor(0, 0, 0);
  y += 8;

  const home = gameStats?.home || {};
  const away = gameStats?.away || {};
  const rows = [
    ['Points', home.points ?? 0, away.points ?? 0],
    ['Field Goals', fg(home.fieldGoalsMade, home.fieldGoalsAttempted), fg(away.fieldGoalsMade, away.fieldGoalsAttempted)],
    ['Field Goal %', pct(home.fieldGoalsMade, home.fieldGoalsAttempted), pct(away.fieldGoalsMade, away.fieldGoalsAttempted)],
    ['3-Pointers', fg(home.threePointersMade, home.threePointersAttempted), fg(away.threePointersMade, away.threePointersAttempted)],
    ['3-Point %', pct(home.threePointersMade, home.threePointersAttempted), pct(away.threePointersMade, away.threePointersAttempted)],
    ['Free Throws', fg(home.freeThrowsMade, home.freeThrowsAttempted), fg(away.freeThrowsMade, away.freeThrowsAttempted)],
    ['Free Throw %', pct(home.freeThrowsMade, home.freeThrowsAttempted), pct(away.freeThrowsMade, away.freeThrowsAttempted)],
    ['Rebounds', home.totalRebounds ?? 0, away.totalRebounds ?? 0],
    ['Assists', home.assists ?? 0, away.assists ?? 0],
    ['Steals', home.steals ?? 0, away.steals ?? 0],
    ['Blocks', home.blocks ?? 0, away.blocks ?? 0],
    ['Turnovers', home.turnovers ?? 0, away.turnovers ?? 0],
    ['Fouls', home.fouls ?? 0, away.fouls ?? 0],
  ];

  doc.setFontSize(9);
  rows.forEach((row, index) => {
    y = ensurePageSpace(doc, y, 8, margin);
    if (index % 2 === 0) {
      doc.setFillColor(243, 244, 246);
      doc.rect(margin, y - 5, pageWidth - margin * 2, 7, 'F');
    }
    doc.setFont('helvetica', 'normal');
    doc.text(String(row[0]), colStat + 2, y);
    doc.setFont('helvetica', 'bold');
    doc.text(String(row[1]), colHome, y);
    doc.text(String(row[2]), colAway, y);
    y += 7;
  });

  return y + 6;
};

const drawBoxScore = (doc, teamName, players, startY, margin) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const headers = ['#', 'Player', 'PTS', 'REB', 'AST', 'STL', 'BLK', 'FG', '3PT', 'FT', 'PF'];
  const colWidths = [12, 55, 14, 14, 14, 14, 14, 22, 22, 22, 14];
  let y = drawSectionTitle(doc, `${teamName || 'Team'} — Box Score`, startY, margin);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(11, 61, 145);
  doc.rect(margin, y - 5, pageWidth - margin * 2, 8, 'F');
  doc.setTextColor(255, 255, 255);
  let x = margin;
  headers.forEach((header, i) => {
    doc.text(header, x + 1, y);
    x += colWidths[i];
  });
  doc.setTextColor(0, 0, 0);
  y += 8;

  const roster = [...(players || [])].sort(
    (a, b) => (a.jerseyNumber || 0) - (b.jerseyNumber || 0)
  );

  if (roster.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.text('No player statistics recorded.', margin + 2, y);
    return y + 10;
  }

  doc.setFont('helvetica', 'normal');
  roster.forEach((player, index) => {
    y = ensurePageSpace(doc, y, 8, margin);
    if (index % 2 === 0) {
      doc.setFillColor(243, 244, 246);
      doc.rect(margin, y - 5, pageWidth - margin * 2, 7, 'F');
    }

    const cells = [
      String(player.jerseyNumber ?? ''),
      (player.name || 'Player').substring(0, 28),
      String(player.points || 0),
      String(player.rebounds || 0),
      String(player.assists || 0),
      String(player.steals || 0),
      String(player.blocks || 0),
      fg(player.fieldGoalsMade, player.fieldGoalsAttempted),
      fg(player.threePointersMade, player.threePointersAttempted),
      fg(player.freeThrowsMade, player.freeThrowsAttempted),
      String(player.fouls || 0),
    ];

    x = margin;
    cells.forEach((cell, i) => {
      doc.text(cell, x + 1, y);
      x += colWidths[i];
    });
    y += 7;
  });

  return y + 8;
};

/**
 * Export live match game statistics as a landscape A4 PDF.
 */
export const exportGameStatsToPDF = async ({
  homeTeam,
  awayTeam,
  gameStats,
  homePlayers = [],
  awayPlayers = [],
  matchMeta = {},
  filename = 'game-statistics',
} = {}) => {
  if (!homeTeam || !awayTeam || !gameStats) {
    alert('No game statistics available to export.');
    return;
  }

  try {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = margin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(11, 61, 145);
    doc.text('Tanzania Basketball Federation — Game Statistics', margin, y);
    doc.setTextColor(0, 0, 0);
    y += 8;

    const homeScore = homeTeam.score ?? gameStats.home?.points ?? 0;
    const awayScore = awayTeam.score ?? gameStats.away?.points ?? 0;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${homeTeam.name || 'Home'}  ${homeScore}  —  ${awayScore}  ${awayTeam.name || 'Away'}`,
      margin,
      y
    );
    y += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metaParts = [
      matchMeta.matchType ? `Type: ${matchMeta.matchType}` : null,
      matchMeta.status ? `Status: ${matchMeta.status}` : null,
      matchMeta.period != null ? `Period: Q${matchMeta.period}` : null,
      `Generated: ${new Date().toLocaleString()}`,
    ].filter(Boolean);
    doc.text(metaParts.join('  ·  '), margin, y);
    y += 4;

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    y = drawSectionTitle(doc, 'Team Comparison', y, margin);
    y = drawTeamComparison(doc, homeTeam, awayTeam, gameStats, y, margin);

    y = ensurePageSpace(doc, y, 40, margin);
    y = drawBoxScore(doc, homeTeam.name, homePlayers, y, margin);

    y = ensurePageSpace(doc, y, 40, margin);
    drawBoxScore(doc, awayTeam.name, awayPlayers, y, margin);

    const safeName = `${filename}-${new Date().toISOString().split('T')[0]}`;
    doc.save(`${safeName}.pdf`);
  } catch (error) {
    console.error('Error exporting game statistics PDF:', error);
    alert('Failed to export game statistics PDF.');
  }
};
